/**
 * Static data for the Game Pass → Style → Assets section.
 *
 * `AssetsSection` renders one upload tile per themeable Game Pass asset. The
 * tile catalogue (key + display label) lives here so that, when the asset API
 * lands, the only thing that changes is the data source — the component keeps
 * mapping the same `AssetField` shape into the same tile.
 *
 * Display copy stays in `AppStrings` (the canonical string store); this
 * module only owns the stable keys and the key → label wiring. Keys are kept
 * as backend-friendly tokens so they can double as the payload field names
 * once persistence is wired up.
 */

import { AppStrings } from "../../../../utils/constants/AppStrings";

/**
 * Stable identifier for a known Game Pass asset slot. Used across the editor
 * tiles ({@link ASSET_FIELDS}), the live preview and the TCM default resolution
 * (`gamePassDefaultAssets`, `gamePassPreviewAssets`).
 *
 * `cash` / `coins` are the tenant's primary/secondary currency symbols. They're
 * fixed TCM brand constants for Game Pass (not admin-changeable), so they stay
 * in this union — the preview renders them from the TCM/bundled defaults — but
 * are deliberately excluded from {@link ASSET_FIELDS} (no editable tile).
 */
export type AssetKey =
  | "cash"
  | "coins"
  | "challengeComplete"
  | "profitBoost"
  | "freeCashPick"
  | "depositMatch"
  | "freeSpins"
  | "seasonConcluded"
  | "carbonFibreBackground"
  | "leaderboardNoRank"
  | "leaderboardFirstPlace"
  | "leaderboardSecondPlace"
  | "leaderboardThirdPlace"
  | "gamePassCompletedBackground"
  | "gamePassOptInBackground";

export type AssetField = {
  key: AssetKey;
  label: string;
};

/**
 * The *editable* asset tiles rendered in the section, in Figma order.
 *
 * Cash and Coins are intentionally omitted: they're fixed TCM currency icons
 * that Game Pass treats as brand constants, so admins can't override them here
 * (the preview still shows them from the TCM defaults).
 */
export const ASSET_FIELDS: ReadonlyArray<AssetField> = [
  {
    key: "challengeComplete",
    label: AppStrings.GAME_PASS_STYLE_ASSET_CHALLENGE_COMPLETE,
  },
  {
    key: "profitBoost",
    label: AppStrings.GAME_PASS_STYLE_ASSET_PROFIT_BOOST,
  },
  {
    key: "freeCashPick",
    label: AppStrings.GAME_PASS_STYLE_ASSET_FREE_CASH_PICK,
  },
  {
    key: "depositMatch",
    label: AppStrings.GAME_PASS_STYLE_ASSET_DEPOSIT_MATCH,
  },
  { key: "freeSpins", label: AppStrings.GAME_PASS_STYLE_ASSET_FREE_SPINS },
  {
    key: "seasonConcluded",
    label: AppStrings.GAME_PASS_STYLE_ASSET_SEASON_CONCLUDED,
  },
  {
    key: "carbonFibreBackground",
    label: AppStrings.GAME_PASS_STYLE_ASSET_CARBON_FIBRE_BACKGROUND,
  },
  {
    key: "leaderboardNoRank",
    label: AppStrings.GAME_PASS_STYLE_ASSET_LEADERBOARD_NO_RANK,
  },
  {
    key: "leaderboardFirstPlace",
    label: AppStrings.GAME_PASS_STYLE_ASSET_LEADERBOARD_FIRST_PLACE,
  },
  {
    key: "leaderboardSecondPlace",
    label: AppStrings.GAME_PASS_STYLE_ASSET_LEADERBOARD_SECOND_PLACE,
  },
  {
    key: "leaderboardThirdPlace",
    label: AppStrings.GAME_PASS_STYLE_ASSET_LEADERBOARD_THIRD_PLACE,
  },
  {
    key: "gamePassCompletedBackground",
    label: AppStrings.GAME_PASS_STYLE_ASSET_GAME_PASS_COMPLETED_BACKGROUND,
  },
  {
    key: "gamePassOptInBackground",
    label: AppStrings.GAME_PASS_STYLE_ASSET_GAME_PASS_OPT_IN_BACKGROUND,
  },
];
